// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import { useBlogHomeDataView } from "@store/Framework/Store/Data/BlogHome/BlogHomeHooks";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import Button from "@store/MobiusOverrides/Button/Button";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import * as React from "react";
import { FC, useEffect, useMemo } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titleAlignment = "titleAlignment",
}

const titleAlignmentField = getTextAlignmentField(fields.titleAlignment);

interface BlogHomeBrowseCategoriesProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleAlignment]: string;
    };
}

export interface BlogHomeBrowseCategoriesStyles {
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    wrapper?: InjectableCss;
    list?: InjectableCss;
    item?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const blogHomeBrowseCategoriesStyles: BlogHomeBrowseCategoriesStyles = {
    title: {
        variant: "h2",
        css: css`
            ${spacingStyles(null, 0)}
        `,
    },
    loadingOverlay: {
        css: css`
            background-color: ${getColor("secondary.dark")};
        `,
    },
    noResultsText: typographyGetNoResultsProps(),
    wrapper: {
        css: css`
            padding: ${pxToRem(48, 32, 32, 32)};
            color: ${getColor("invert")};
            background-color: ${getColor("secondary.dark")};

            ${breakpoint("md")} {
                padding: ${pxToRem(48, 160, 32, 160)};
            }
        `,
    },
    list: {
        css: css`
            display: flex;
            gap: ${pxToRem(12)};
            justify-content: center;
            flex-wrap: wrap;
            padding-top: ${pxToRem(32)};
        `,
    },
    button: { variant: "invertedOutline" as any },
};

const styles = blogHomeBrowseCategoriesStyles;

const BlogHomeBrowseCategories: FC<BlogHomeBrowseCategoriesProps> = ({ fields }) => {
    const { title, titleAlignment: baseTitleAlignment } = fields;
    const titleAlignment = titleAlignmentField.parse(baseTitleAlignment);
    const dispatch = useAppDispatch();
    const currentPage = useCurrentPage();
    const baseBlogCategoriesDataView = useBlogHomeDataView();
    const blogCategoriesDataView = usePersistentDataView(baseBlogCategoriesDataView);
    const { isLoading, value: categories } = blogCategoriesDataView;

    useEffect(() => {
        if (!isLoading && !categories) {
            dispatch(loadPagesForParent({ parentNodeId: currentPage.nodeId }));
        }
    }, []);

    const links: SafeDictionary<PageLinkModel> =
        useAppSelector(state =>
            categories?.reduce(
                (links, page) => ({
                    ...links,
                    [page.nodeId]: getPageLinkByNodeId(state, page.nodeId),
                }),
                {},
            ),
        ) || {};

    const titleStyles = useMemo(() => {
        return mergeProps<BlogHomeBrowseCategoriesStyles["title"]>(styles.title, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.title, titleAlignment]);

    const noResultsTextStyles = useMemo(() => {
        return mergeProps<BlogHomeBrowseCategoriesStyles["noResultsText"]>(styles.noResultsText, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.noResultsText, titleAlignment]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...titleStyles}>{title}</Typography>
            <DataViewLoadingOverlay {...styles.loadingOverlay} dataView={blogCategoriesDataView}>
                {!categories?.length ? (
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography {...noResultsTextStyles}>{siteMessage("Blog_Categries_Not_Found")}</Typography>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.list} as="ul">
                        {categories.map((category: PageProps) => {
                            const { id, nodeId } = category;
                            if (!links[nodeId]?.title) {
                                return;
                            }

                            return (
                                <StyledWrapper key={id} {...styles.item} as="li">
                                    <Button {...styles.button} href={links[nodeId]?.url}>
                                        {links[nodeId]?.title}
                                    </Button>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                )}
            </DataViewLoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BlogHomeBrowseCategories,
    definition: {
        group: constants.widgetGroup.blog,
        allowedContexts: [constants.pageType.blogHomePage],
        displayName: "Browse Categories",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Browse Categories",
                isRequired: true,
            },
            {
                ...titleAlignmentField.definition,
                displayName: "Title Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.center.id,
            },
        ],
    },
};

export default widgetModule;
